// OOO-DELEGATE: out-of-office windows with a delegate (a person of the same company, never a DE).
// While away, approvals, escalations and DE questions routed to the person go to the delegate
// ("covering for X"), else the next person up the org chart. Behind COS_OOO_DELEGATE.
import { z } from "zod";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

export const OooWindowCreate = z
  .strictObject({
    /** Default: the caller. Setting it for someone else needs company manager access. */
    membership_id: Uuid.optional(),
    /** Only this seat (the caller must hold it); omitted: every seat the person holds. */
    seat_id: Uuid.nullable().optional(),
    /** A person of this company; omitted: the next person up the org chart covers. */
    delegate_membership_id: Uuid.nullable().optional(),
    starts_at: Timestamp,
    ends_at: Timestamp,
  })
  .meta({ id: "OooWindowCreate" });

export const OooWindow = z
  .strictObject({
    id: Uuid,
    membership_id: Uuid,
    seat_id: Uuid.nullable(),
    delegate_membership_id: Uuid.nullable(),
    starts_at: Timestamp,
    ends_at: Timestamp,
    ended_at: Timestamp.nullable(),
  })
  .meta({ id: "OooWindow" });

export const OooWindowList = z
  .strictObject({ data: z.array(OooWindow) })
  .meta({ id: "OooWindowList" });

export const OooReturnSummary = z
  .strictObject({
    data: z.array(
      z.strictObject({
        window_id: Uuid,
        routed: z.array(
          z.strictObject({
            subject_kind: z.string(),
            subject_id: z.string(),
            via: z.string(),
            at: Timestamp,
          }),
        ),
      }),
    ),
  })
  .meta({ id: "OooReturnSummary" });

export const oooRoutes = {
  "ooo.list": defineRoute({
    ...core,
    id: "ooo.list",
    method: "get",
    path: `${base}/out-of-office`,
    tag: "people",
    summary: "Your current and upcoming out-of-office windows",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: OooWindowList },
    errors: [],
    emits: [],
  }),
  "ooo.set": defineRoute({
    ...core,
    id: "ooo.set",
    method: "post",
    path: `${base}/out-of-office`,
    tag: "people",
    summary: "Set an out-of-office window and delegate",
    access: "member",
    params: CompanyParams,
    body: OooWindowCreate,
    idempotency: "optional",
    response: { status: 201, schema: OooWindow },
    errors: [],
    emits: ["ooo.window_set"],
  }),
  "ooo.end": defineRoute({
    ...core,
    id: "ooo.end",
    method: "post",
    path: `${base}/out-of-office/{id}/end`,
    tag: "people",
    summary: "End an out-of-office window early; routing is restored",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: OooWindow },
    errors: [],
    emits: ["ooo.window_ended"],
  }),
  "ooo.returnSummary": defineRoute({
    ...core,
    id: "ooo.returnSummary",
    method: "get",
    path: `${base}/out-of-office/return-summary`,
    tag: "people",
    summary: "What was routed away while you were out, for windows you have not yet dismissed",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: OooReturnSummary },
    errors: [],
    emits: [],
  }),
  "ooo.dismissSummary": defineRoute({
    ...core,
    id: "ooo.dismissSummary",
    method: "post",
    path: `${base}/out-of-office/{id}/dismiss-summary`,
    tag: "people",
    summary: "Mark a window's return summary as seen",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: OooWindow },
    errors: [],
    emits: ["ooo.summary_seen"],
  }),
} as const;
